import api from 'services/api';

/* ═══════════════════════════════════════════════════════════════════════════
 * TÉLÉVERSEMENT — L'ÉCRAN LIT LA MÊME TABLE QUE LE SERVEUR
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ AUCUNE LIMITE N'EST ÉCRITE DANS CE FICHIER. Elles viennent de
 *    `GET /api/limites-televersement`, qui rend la table déclarée dans
 *    `backend/src/config/limites-televersement.js` — la même que celle que
 *    multer applique. C'est tout l'objet de la route.
 *
 * ⚠️ AUCUN REPLI SUR UNE LIMITE « PAR DÉFAUT ». Si la table n'a pas pu être
 *    lue, on ne devine pas 2 Mo : l'appelant reçoit `null` et l'écran DIT
 *    qu'il ne connaît pas la limite. Une limite devinée qui se révèle fausse,
 *    c'est exactement le 413 qu'on vient de payer.
 */

let cache = null;
let enCours = null;

export async function chargerLimites() {
  if (cache) return cache;
  if (!enCours) {
    enCours = api.get('/limites-televersement')
      .then((r) => { cache = r.data; return cache; })
      .catch(() => { enCours = null; return null; });
  }
  return enCours;
}

/** La ligne de la table pour un usage, ou `null` si la table n'a pas été lue. */
export async function limitePour(usage) {
  const t = await chargerLimites();
  return (t && t.usages && t.usages[usage]) || null;
}

/** Poids lisible en français — miroir exact de `formaterPoids` côté serveur. */
export function formaterPoids(octets) {
  if (octets === null || octets === undefined) return '';
  const Mo = 1024 * 1024;
  if (octets < 1024) return `${octets} o`;
  if (octets < Mo) return `${Math.round(octets / 1024)} Ko`;
  const mo = octets / Mo;
  const arrondi = mo < 10 ? Math.round(mo * 10) / 10 : Math.round(mo);
  return `${String(arrondi).replace('.', ',')} Mo`;
}

/**
 * REDIMENSIONNE UNE PHOTO DANS LE NAVIGATEUR, AVANT L'ENVOI.
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ POURQUOI — LA CONNEXION, PAS LA LIMITE.
 *    Une photo de téléphone fait 3 à 5 Mo. Sur une connexion togolaise, en 3G,
 *    l'envoyer entière prend une trentaine de secondes et échoue souvent. Après
 *    redimensionnement — côté le plus long à 512 px, JPEG qualité 0,85 — elle
 *    pèse environ 150 Ko et part instantanément.
 *    ➡️ L'utilisateur N'ATTEINT PLUS JAMAIS la limite dans le cas courant.
 *       La limite serveur reste, comme garde-fou ; elle ne sert plus à refuser
 *       une photo normale.
 *
 * ⚠️ NE S'APPLIQUE QU'AUX PHOTOS. Un logo se rastériserait et perdrait sa
 *    transparence ; un SVG n'a pas de « côté le plus long » à réduire ; un scan
 *    de contrat doit rester lisible. La table le dit : `redimensionner: null`
 *    sur ces usages, et cette fonction n'est alors pas appelée.
 *
 * ⚠️ EN CAS D'ÉCHEC (format exotique, image illisible par le navigateur), on
 *    rend le fichier D'ORIGINE plutôt que rien. Le serveur reste l'autorité :
 *    au pire, il refuse et il nomme pourquoi.
 *
 * @param {File} fichier
 * @param {{cote_max:number, qualite:number}} options
 * @returns {Promise<File>} un JPEG redimensionné, ou le fichier d'origine
 */
export function redimensionnerImage(fichier, options) {
  const coteMax = (options && options.cote_max) || 512;
  const qualite = (options && options.qualite) || 0.85;

  return new Promise((resolve) => {
    if (!fichier || !fichier.type || !fichier.type.startsWith('image/')) {
      resolve(fichier);
      return;
    }
    const url = URL.createObjectURL(fichier);
    const img = new Image();

    img.onload = () => {
      try {
        const { width: w, height: h } = img;
        // Une image DÉJÀ plus petite n'est pas agrandie : on ne fabrique pas
        // des pixels qui n'existent pas.
        const ratio = Math.min(1, coteMax / Math.max(w, h));
        const cw = Math.max(1, Math.round(w * ratio));
        const ch = Math.max(1, Math.round(h * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        // Fond blanc : un PNG transparent aplati en JPEG donnerait du noir.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) { resolve(fichier); return; }
          const base = (fichier.name || 'photo').replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
        }, 'image/jpeg', qualite);
      } catch (_) {
        URL.revokeObjectURL(url);
        resolve(fichier);
      }
    };

    img.onerror = () => { URL.revokeObjectURL(url); resolve(fichier); };
    img.src = url;
  });
}

/**
 * Prépare un fichier pour un usage donné : contrôle le format, redimensionne
 * si la table le demande, puis contrôle le poids.
 * Rend `{ fichier, erreur }` — `erreur` est un message FRANÇAIS COMPLET,
 * qui nomme le poids REÇU et la limite, tous deux lus dans la table.
 */
export async function preparerFichier(fichier, usage) {
  const regle = await limitePour(usage);
  if (!regle) {
    // On ne devine pas. On laisse passer et le serveur tranchera en nommant.
    return { fichier, erreur: null, limiteInconnue: true };
  }

  const ext = '.' + (fichier.name || '').split('.').pop().toLowerCase();
  if (!regle.extensions.includes(ext)) {
    return {
      fichier: null,
      erreur: `Format non accepté : ${ext}. Formats acceptés : ${regle.libelle_formats}.`,
    };
  }

  let f = fichier;
  if (regle.redimensionner) {
    f = await redimensionnerImage(fichier, regle.redimensionner);
  }

  if (f.size > regle.octets) {
    return {
      fichier: null,
      erreur: `Fichier trop lourd : ${formaterPoids(f.size)}. Maximum : ${formaterPoids(regle.octets)}.`,
    };
  }
  return { fichier: f, erreur: null };
}
